import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBusinessProfile } from '../../context/BusinessProfileContext';
import { subscriptionService } from '../../services/SubscriptionService';
import { getEOBusinessProfile } from '../../firebaseFunctions/getEOBusinessProfile/getEOBusinessProfile';
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/solid';
import { ChevronDownIcon } from '@heroicons/react/outline';
import pebbleLogo from '../../assets/images/pebble-logo.png';
import { useTranslation } from 'react-i18next';
import DOMAIN_CONFIG from '../../config/domains';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseFunctions/config';

const EVENT_TYPES = [
  { id: 'festival', label: 'Festival' },
  { id: 'farmers_market', label: 'Farmers Market' },
  { id: 'flea_market', label: 'Flea Market' },
  { id: 'artisan_market', label: 'Artisan Market' },
  { id: 'pop_up', label: 'Pop-Up Event' },
  { id: 'vendor_fair', label: 'Vendor Fair' },
  { id: 'art_fair', label: 'Art Fair' },
  { id: 'craft_fair', label: 'Craft Fair' },
  { id: 'food_festival', label: 'Food Festival' },
  { id: 'music_festival', label: 'Music Festival' },
  { id: 'holiday_market', label: 'Holiday Market' },
  { id: 'trade_show', label: 'Trade Show' },
  { id: 'concert', label: 'Concert / Live Event' },
  { id: 'sporting_event', label: 'Sporting Event' },
  { id: 'other', label: 'Other' }
];

const EOCompleteProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, hasActiveSubscription, userType, initialAuthCheckDone, updateSubscription } = useAuth();
  const { businessProfileData, updateBusinessProfile, submitBusinessProfile } = useBusinessProfile();
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionVerified, setSubscriptionVerified] = useState(false);
  const [firebaseProfileChecked, setFirebaseProfileChecked] = useState(false);
  const [firestoreSubscriptionChecked, setFirestoreSubscriptionChecked] = useState(false);
  const [firestoreHasSubscription, setFirestoreHasSubscription] = useState(false);
  const [lastCheckedUserId, setLastCheckedUserId] = useState(null);
  const verificationAttempted = useRef(false);
  const profileCheckAttempted = useRef(false);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    companyWebsite: '',
    eventType: '',
    contactNumber: ''
  });
  const [formInitialized, setFormInitialized] = useState(false);

  // Handle subscription verification from Stripe redirect (silently in background)
  useEffect(() => {
    const verifySubscription = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId || !user || verificationAttempted.current) return;
      
      verificationAttempted.current = true;
      
      try {
        const result = await subscriptionService.handleCheckoutSuccess(sessionId, user.uid);
        
        if (result.success) {
          if (user.uid && result.subscription) {
            let periodEnd;
            if (result.subscription.currentPeriodEnd) {
              periodEnd = new Date(result.subscription.currentPeriodEnd);
            } else {
              periodEnd = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
            }
            
            await subscriptionService.activateSubscription(user.uid, {
              status: result.subscription.status || 'active',
              plan: result.subscription.planId || result.subscription.plan || 'basic',
              stripeSubscriptionId: result.subscription.id,
              stripeCustomerId: result.subscription.customerId,
              currentPeriodEnd: periodEnd
            });
          }
          
          const planTier = result.subscription?.planId || result.subscription?.plan || 'basic';
          updateSubscription({
            hasActive: true,
            status: result.subscription?.status || 'active',
            tier: planTier
          });
          
          // Reset onboarding for first-timer welcome experience
          try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const { db } = await import('../../firebaseFunctions/config');
            await updateDoc(doc(db, 'users', user.uid), {
              'onboarding.hasSeenWelcome': false,
              isEoSignedUp: true
            });
          } catch (e) {
            console.log('Could not reset onboarding state:', e);
          }
          
          setSubscriptionVerified(true);
          
          // Clear session_id from URL without reload
          window.history.replaceState({}, '', '/eo/complete-profile');
        }
      } catch (err) {
        console.error('Subscription verification error:', err);
        // Still allow them to proceed - subscription might already be active
        setSubscriptionVerified(true);
      }
    };

    verifySubscription();
  }, [user, searchParams, updateSubscription]);

  // Check Firestore directly for subscription status (prevents redirect loop)
  useEffect(() => {
    const checkFirestoreSubscription = async () => {
      if (!user?.uid) {
        setFirestoreSubscriptionChecked(true);
        setFirestoreHasSubscription(false);
        setLastCheckedUserId(null);
        return;
      }

      if (lastCheckedUserId === user.uid) return;

      setFirestoreSubscriptionChecked(false);
      setFirestoreHasSubscription(false);

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          if (userData.hasActiveSubscription === true || userData.hasActiveSubscription === 'true') {
            setFirestoreHasSubscription(true);
          }
        }
      } catch (error) {
        console.error('Error checking Firestore subscription:', error);
      }

      setLastCheckedUserId(user.uid);
      setFirestoreSubscriptionChecked(true);
    };

    if (initialAuthCheckDone) {
      checkFirestoreSubscription();
    }
  }, [user?.uid, initialAuthCheckDone, lastCheckedUserId, hasActiveSubscription]);

  // Check Firebase for existing profile data
  useEffect(() => {
    const checkFirebaseProfile = async () => {
      if (!user || profileCheckAttempted.current) return;

      profileCheckAttempted.current = true;

      try {
        const result = await getEOBusinessProfile(user.uid);

        if (result.success && result.data) {
          const firebaseProfile = result.data;

          // Check if profile is already complete in Firebase
          const hasCompleteProfile =
            firebaseProfile.fullName?.trim() &&
            firebaseProfile.businessName?.trim();

          if (hasCompleteProfile) {
            // Profile already exists in Firebase, redirect to dashboard
            DOMAIN_CONFIG.redirectToApp('/eo/dashboard');
            return;
          }

          // Profile exists but incomplete - pre-fill form with existing data
          if (!formInitialized) {
            setFormData({
              fullName: firebaseProfile.fullName || user?.displayName || '',
              companyName: firebaseProfile.businessName || '',
              companyWebsite: firebaseProfile.companyWebsite || '',
              eventType: firebaseProfile.eventType || '',
              contactNumber: firebaseProfile.contactNumber || ''
            });
            setFormInitialized(true);
          }
        } else {
          // No profile in Firebase - use localStorage/defaults
          if (!formInitialized) {
            const profileBelongsToCurrentUser = businessProfileData?.UserID === user.uid;
            setFormData({
              fullName: (profileBelongsToCurrentUser && businessProfileData?.fullName) || user?.displayName || '',
              companyName: (profileBelongsToCurrentUser && businessProfileData?.companyName) || '',
              companyWebsite: (profileBelongsToCurrentUser && businessProfileData?.companyWebsite) || '',
              eventType: (profileBelongsToCurrentUser && businessProfileData?.eventType) || '',
              contactNumber: (profileBelongsToCurrentUser && businessProfileData?.contactNumber) || ''
            });
            setFormInitialized(true);
          }
        }
      } catch (error) {
        console.error('Error checking Firebase profile:', error);
        // Fall back to localStorage data
        if (!formInitialized) {
          const profileBelongsToCurrentUser = businessProfileData?.UserID === user.uid;
          setFormData({
            fullName: (profileBelongsToCurrentUser && businessProfileData?.fullName) || user?.displayName || '',
            companyName: (profileBelongsToCurrentUser && businessProfileData?.companyName) || '',
            companyWebsite: (profileBelongsToCurrentUser && businessProfileData?.companyWebsite) || '',
            eventType: (profileBelongsToCurrentUser && businessProfileData?.eventType) || '',
            contactNumber: (profileBelongsToCurrentUser && businessProfileData?.contactNumber) || ''
          });
          setFormInitialized(true);
        }
      }

      setFirebaseProfileChecked(true);
    };

    checkFirebaseProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, businessProfileData, formInitialized]);

  useEffect(() => {
    if (!initialAuthCheckDone || !firestoreSubscriptionChecked) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    // Only redirect if userType is explicitly NOT EO (allow null for new users)
    if (userType && userType !== 'EO') {
      navigate('/', { replace: true });
      return;
    }

    // Freemium model — no paywall, all EOs have access
  }, [user, userType, hasActiveSubscription, firestoreHasSubscription, navigate, initialAuthCheckDone, firestoreSubscriptionChecked, searchParams, subscriptionVerified]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const profileData = {
        ...businessProfileData,
        fullName: formData.fullName,
        businessName: formData.companyName, // Firebase expects businessName
        companyName: formData.companyName,
        companyWebsite: formData.companyWebsite,
        eventType: formData.eventType,
        contactNumber: formData.contactNumber,
        UserID: user.uid,
        stripeAccountConnected: businessProfileData?.stripeAccountConnected || false,
        EOProfileID: businessProfileData?.EOProfileID || 'false',
        approvedTermsOfService: 'true',
        profileCompleted: true
      };

      await updateBusinessProfile(profileData);
      const result = await submitBusinessProfile(profileData);
      console.log('Profile submission result:', result);

      DOMAIN_CONFIG.redirectToApp('/eo/dashboard');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = 
    formData.fullName.trim() !== '' && 
    formData.companyName.trim() !== '' &&
    formData.eventType !== '';

  if (!initialAuthCheckDone || !formInitialized || !firebaseProfileChecked || !firestoreSubscriptionChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-grey-50 via-white to-primary-25 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <img src={pebbleLogo} alt="Pebble" className="h-8 w-auto" />
          <div className="w-8 h-8 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-grey-50 via-white to-primary-25 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <Link to="/">
              <img src={pebbleLogo} alt="Pebble" className="h-10 mx-auto mb-6 hover:opacity-80 transition-opacity" />
            </Link>
            
            <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <SparklesIcon className="w-4 h-4" />
              {t('pages.completeProfile.almostThere')}
            </div>

            <h1 className="text-2xl font-bold text-grey-900 mb-2">
              {t('pages.completeProfile.title')}
            </h1>
            <p className="text-grey-600">
              {t('pages.completeProfile.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-grey-700 mb-1">
                {t('pages.completeProfile.fullName')}
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-grey-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('pages.completeProfile.fullNamePlaceholder')}
                required
              />
            </div>

            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-grey-700 mb-1">
                {t('pages.completeProfile.companyName')}
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-grey-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('pages.completeProfile.companyNamePlaceholder')}
                required
              />
            </div>

            <div>
              <label htmlFor="companyWebsite" className="block text-sm font-medium text-grey-700 mb-1">
                {t('pages.completeProfile.companyWebsite')} <span className="text-grey-400">(optional)</span>
              </label>
              <input
                type="text"
                id="companyWebsite"
                name="companyWebsite"
                value={formData.companyWebsite}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-grey-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('pages.completeProfile.companyWebsitePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="eventType" className="block text-sm font-medium text-grey-700 mb-1">
                {t('pages.completeProfile.eventType')}
              </label>
              <div className="relative">
                <select
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-grey-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white cursor-pointer"
                  required
                >
                  <option value="">{t('pages.completeProfile.eventTypePlaceholder')}</option>
                  {EVENT_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-grey-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label htmlFor="contactNumber" className="block text-sm font-medium text-grey-700 mb-1">
                {t('pages.completeProfile.contactNumber')} <span className="text-grey-400">(optional)</span>
              </label>
              <input
                type="tel"
                id="contactNumber"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-grey-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('pages.completeProfile.contactNumberPlaceholder')}
              />
            </div>

            <button
              type="submit"
              disabled={!isFormValid || isLoading}
              className={`w-full py-3 px-6 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg active:scale-[0.98] flex items-center justify-center gap-2 ${
                isFormValid && !isLoading
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-grey-300 text-grey-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  {t('pages.completeProfile.goToDashboard')}
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-grey-500 mt-6">
            You can update these details anytime in Settings
          </p>
        </div>
      </div>
    </div>
  );
};

export default EOCompleteProfile;
